import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { BASE_USER_ROLES } from '@/common/constants/user';
import AuthModalStore from '@/stores/AuthModalStore';
import AuthStore from '@/stores/AuthStore';
import { Group } from '@/types/Content/Group';
import { Product } from '@/types/Content/Product/Product';
import ProductStore from '@/stores/ProductStore';
import ProfileBuilderStore from '@/stores/ProfileBuilderStore';
import useHasMounted from '@/hooks/useHasMounted';
import { ProductsParams } from '@/types/Content/Product/ProductParams';
import Discussion from '@/types/Discussions';
import DiscussionStore from '@/stores/DiscussionStore';
import SubscriptionGroupStore from '@/stores/SubscriptionGroupStore';
import { HTTPListResponse } from '@/types/HTTPListResponse';
import { ISubscriptionGroupQuery } from '@/services/subscriptionGroupService';
import FridgeDoorStore from '@/stores/FridgeDoorStore';
import MoreGroupsUnauthenticated from './components/MoreGroupsUnauthenticated';
import PageSkeleton from './components/PageSkeleton';
// import MainCarousel from './components/MainCarousel';
import GroupList from './components/GroupList';
import { useLifeEvent } from './hooks/useLifeEvent';
import LifeEvents from './components/LifeEvents/LifeEvents';
import { useGetSubscriptionGroupListWithLifeEvents } from './hooks/useGetSubscriptionGroupListWithLifeEvents';
import { useLifeEventVisibility } from './hooks/useLifeEventVisibility';

type DiscoverPageProps = {
  filterType: string;
  products: Product[];
  groups: { data: HTTPListResponse<Group>; params: ISubscriptionGroupQuery };
  productsParams: ProductsParams;
  discussions: {
    data: Discussion[];
    tags: string[];
  };
};

export const DEFAULT_USER_ROLE = BASE_USER_ROLES.ALL;
export const DEFAULT_CANCER_TYPE = 'all';

function DiscoverPage({
  filterType,
  products,
  groups,
  productsParams,
  discussions,
}: DiscoverPageProps): JSX.Element {
  const { busy } = useLifeEvent();

  const { visibilityInfo, setItemVisibility } = useLifeEventVisibility();

  useGetSubscriptionGroupListWithLifeEvents();

  const { lifeEventTypeList } = ProfileBuilderStore;
  const { currentVillage } = FridgeDoorStore;

  const lifeEventsSorted = useMemo(() => {
    const { life_event_type: lifeEventType } = currentVillage || {};

    if (!lifeEventType) return lifeEventTypeList;

    const itemIdx = lifeEventTypeList.findIndex(
      (el) => el.id === lifeEventType,
    );
    if (itemIdx < 0) return lifeEventTypeList;

    const item = lifeEventTypeList[itemIdx];
    const newList = [...lifeEventTypeList];
    newList.splice(itemIdx, 1);
    newList.splice(0, 0, item);

    return newList;
  }, [lifeEventTypeList, currentVillage]);

  const {
    subscriptionGroupListWithLifeEvents,
    loading,
  } = SubscriptionGroupStore;

  const [userRole] = useState(DEFAULT_USER_ROLE);
  const isMounted = useHasMounted();
  const { isGuest, priorityRole } = AuthStore || {
    cancerType: 'breast',
  };

  // const correctUserRole = useMemo(() => {
  //   return priorityRole.label === 'guest' ? userRole : priorityRole.label;
  // }, [priorityRole, userRole]);

  useEffect(() => {
    if (products?.length) {
      ProductStore.setRawProductList(products, productsParams);
    }
  }, [products, productsParams]);

  useEffect(() => {
    if (discussions.data?.length) {
      DiscussionStore.hydrate(discussions);
    }
  }, [discussions]);

  useEffect(() => {
    if (groups.data?.results?.length) {
      SubscriptionGroupStore.hydrate(groups);
    }
  }, [groups]);

  function onClickToContinueWithEmail() {
    AuthModalStore.showModal('signup');
  }
  return !isMounted || busy || ProfileBuilderStore.loadingLifeEventTypes ? (
    <PageSkeleton />
  ) : (
    <>
      {/* <DiscoverBanner lifeEvent={lifeEvent} /> */}

      {/* <MainCarousel
filterType={filterType}
cancerType={cancerType}
userRole={correctUserRole}
/> */}

      <LifeEvents
        visibilityInfo={visibilityInfo}
        setItemVisibility={setItemVisibility}
        lifeEventsSorted={lifeEventsSorted}
      />

      {loading.getAllWithLifeEvents ? (
        <PageSkeleton />
      ) : (
        <GroupList
          subscriptionGroupList={subscriptionGroupListWithLifeEvents}
          visibilityInfo={visibilityInfo}
          lifeEventsSorted={lifeEventsSorted}
        />
      )}

      {isGuest && (
        <MoreGroupsUnauthenticated
          onClickToContinueWithEmail={onClickToContinueWithEmail}
        />
      )}
    </>
  );
}

export default observer(DiscoverPage);
